import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { useAgentStore } from '@/stores/agentStore';
import { useConsole } from '@/console/ConsoleContext';
import { usePermission } from '@/hooks/usePermission';
import { listSpamSessions, serverErrorText, undoSessionSpam } from '@/api/spam';
import { formatDurationFromNow, parseUtcInstant } from '@/utils/relativeTime';
import type { SpamMarkedSessionDto } from '@/types';

/** How often the "time left" column re-renders. The data itself is not refetched. */
const TICK_MS = 30_000;

/** First non-blank string of the candidates, trimmed; '' when none. */
function firstText(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    const s = (c ?? '').trim();
    if (s.length > 0) return s;
  }
  return '';
}

/**
 * OP-1005 - the "Marked as spam" view: every conversation of the tenant that is hidden and waiting
 * to be deleted, with the one thing an operator needs here - Undo.
 *
 * Reached from the rail ("Marked as spam"), shown only to a user holding `workspace.handle` (the
 * permission the server requires on GET /api/sessions/spam and on Undo). The page re-checks it, so
 * a deep link by a user without the permission gets a refusal and no request is made.
 *
 * Undo sends the request, and only on success lifts the conversation's tombstone and asks the shell
 * to reload the session list (the row returns to the inbox). A failed Undo leaves the row where it is
 * and says so inline. Customer-supplied text (names, subjects, previews) is rendered as plain text
 * nodes only.
 */
export const SpamPage: React.FC = () => {
  const { t, lang } = useT();
  const { hasPermission } = usePermission();
  const canHandle = hasPermission('workspace.handle');
  const { reloadSessions } = useConsole();
  const clearSpamTombstone = useConversationStore((s) => s.clearSpamTombstone);
  // Another agent hiding or restoring a conversation changes the tombstone set (hub push), which is
  // the cue to refetch: this view is otherwise a snapshot.
  const tombstoneKey = useConversationStore((s) => Object.keys(s.spamTombstones).sort().join('|'));
  const directory = useAgentStore((s) => s.directory);

  const [rows, setRows] = useState<SpamMarkedSessionDto[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [undoing, setUndoing] = useState<Record<string, boolean>>({});
  const [undoError, setUndoError] = useState<Record<string, string>>({});
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      setRows(await listSpamSessions());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    if (!canHandle) return;
    void load();
  }, [canHandle, load, tombstoneKey]);

  useEffect(() => {
    const i = window.setInterval(() => setTick((x) => x + 1), TICK_MS);
    return () => window.clearInterval(i);
  }, []);

  const undo = async (row: SpamMarkedSessionDto) => {
    setUndoing((s) => ({ ...s, [row.sessionId]: true }));
    setUndoError((s) => {
      const next = { ...s };
      delete next[row.sessionId];
      return next;
    });
    try {
      await undoSessionSpam(row.sessionId);
    } catch (err) {
      setUndoError((s) => ({ ...s, [row.sessionId]: serverErrorText(err) ?? t('spam_undo_failed') }));
      setUndoing((s) => ({ ...s, [row.sessionId]: false }));
      return;
    }
    // Server said yes: lift the tombstone FIRST (the session-list reload filters on it), then reload.
    clearSpamTombstone(row.sessionId);
    setRows((prev) => (prev ?? []).filter((r) => r.sessionId !== row.sessionId));
    setUndoing((s) => ({ ...s, [row.sessionId]: false }));
    reloadSessions();
  };

  if (!canHandle) {
    return (
      <div className="ac-page ac-fade-up" data-testid="spam-page">
        <div className="ac-card" style={{ padding: '40px' }} role="alert" data-testid="spam-no-access">
          {t('spam_no_access')}
        </div>
      </div>
    );
  }

  const list = rows ?? [];
  const now = Date.now();

  return (
    <div className="ac-page ac-fade-up" data-testid="spam-page">
      <div className="ac-page-head">
        <h1 className="ac-page-title" style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <Icon name="ban" size={24} />
          {t('spam_page_title')}
        </h1>
        <div className="ac-page-sub" data-testid="spam-page-sub">
          {t(list.length === 1 ? 'spam_page_sub_one' : 'spam_page_sub', { n: list.length })}
        </div>
        <div className="ac-page-sub" data-testid="spam-page-note" style={{ maxWidth: 760 }}>
          {t('spam_page_note')}
        </div>
      </div>

      {loadError && (
        <div
          role="alert"
          className="ac-card"
          data-testid="spam-load-error"
          style={{ padding: '12px 20px', marginBottom: 'var(--gap)', color: 'var(--warning)', fontWeight: 600 }}
        >
          {t('spam_load_error')}
        </div>
      )}

      {rows !== null && list.length === 0 ? (
        <div className="ac-card" style={{ padding: '60px 40px' }}>
          <div className="ac-empty" style={{ height: 'auto' }} data-testid="spam-empty">
            <div className="ac-empty-ico">
              <Icon name="ban" size={28} />
            </div>
            <div className="ac-empty-title">{t('spam_empty')}</div>
            <div className="ac-empty-sub">{t('spam_empty_sub')}</div>
          </div>
        </div>
      ) : list.length > 0 ? (
        <div className="ac-card">
          <div className="ac-table-row head">
            <div>{t('spam_col_what')}</div>
            <div>{t('spam_col_marked')}</div>
            <div>{t('spam_col_left')}</div>
            <div>{t('spam_col_blocked')}</div>
            <div></div>
          </div>
          <div className="ac-stagger">
            {list.map((row) => {
              const primary = firstText(
                row.customerDisplayName,
                row.subject,
                row.firstMessagePreview,
              ) || t('unidentified');
              const secondary = firstText(row.subject, row.firstMessagePreview);
              const markedAgo = formatDurationFromNow(row.markedAtUtc, lang);
              const markedByName = row.markedBy ? directory[row.markedBy] : undefined;
              const purgeAt = parseUtcInstant(row.purgeAfterUtc);
              const left = formatDurationFromNow(row.purgeAfterUtc, lang);
              const due = Number.isNaN(purgeAt) || purgeAt <= now;
              const isUndoing = !!undoing[row.sessionId];
              return (
                <div
                  key={row.sessionId}
                  className="ac-table-row"
                  data-testid={`spam-row-${row.sessionId}`}
                >
                  <div className="ac-q-cust">
                    <Avatar name={primary} seed={row.sessionId} size={38} className="ac-q-av" />
                    <div style={{ minWidth: 0 }}>
                      <div className="ac-q-name" data-testid={`spam-row-name-${row.sessionId}`}>
                        {primary}
                      </div>
                      {secondary && secondary !== primary && (
                        <div className="ac-q-topic" title={secondary}>
                          {secondary}
                        </div>
                      )}
                    </div>
                  </div>
                  <div data-testid={`spam-row-marked-${row.sessionId}`}>
                    {markedAgo ? t('spam_marked_ago', { time: markedAgo }) : ''}
                    {markedByName && (
                      <div className="ac-q-topic">{t('spam_marked_by', { name: markedByName })}</div>
                    )}
                  </div>
                  <div
                    className={'ac-wait' + (due ? ' high' : '')}
                    data-testid={`spam-row-left-${row.sessionId}`}
                  >
                    <Icon name="clock" size={14} />
                    {due || !left ? t('spam_due') : t('spam_time_left', { time: left })}
                  </div>
                  <div data-testid={`spam-row-blocked-${row.sessionId}`} data-blocked={row.blockSender ? 'true' : 'false'}>
                    <span className={'ac-badge ' + (row.blockSender ? 'high' : 'low')}>
                      {row.blockSender ? t('spam_sender_blocked') : t('spam_sender_not_blocked')}
                    </span>
                  </div>
                  <div style={{ textAlign: 'end' }}>
                    <button
                      className="ac-btn ghost sm"
                      disabled={isUndoing}
                      onClick={() => undo(row)}
                      data-testid={`spam-undo-${row.sessionId}`}
                    >
                      {isUndoing ? t('spam_undoing') : t('spam_undo')}
                    </button>
                    {undoError[row.sessionId] && (
                      <div
                        role="alert"
                        data-testid={`spam-undo-error-${row.sessionId}`}
                        style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4, fontWeight: 600 }}
                      >
                        {undoError[row.sessionId]}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};
